// De vacaturestatus, een keer, voor de hele app (eigenaarsbesluit 13 september 2026).
//
// Er zijn nog maar DRIE opgeslagen waarden: 'open', 'gepauzeerd' en 'gesloten'.
// De oude app-categorieen ('standard', 'hot', 'paused') zijn hernoemd, niet
// verplaatst: de opslag blijft job_overrides.data.category, met de ruwe
// RecruitCRM-status (job_status_label) als terugval zolang er geen override is.
//
// Alleen die drie waarden zijn een statuskeuze. De hernoeming raakte alleen
// job_overrides; in de spiegelkolom rcrm_jobs.category staan de oude waarden
// nog (12 okt 2026: 105 'standard', 2 'hot', 88 'paused'). 'standard' en 'hot'
// waren de werkstand van het oude raster, geen besluit, en vallen door naar het
// label. Tot die dag wogen ze zwaarder: een vacature die RecruitCRM Closed
// noemde las als open zodra de kolom 'standard' zei, en hield zo zijn
// boardpost live. 'paused' blijft gepauzeerd (eigenaarsbesluit 12 okt 2026):
// niemand weet wie hem zette, dus hij kan een echte pauze zijn. De drie nieuwe waarden in de kolom tellen wel: die schrijven
// de app zelf (een nieuwe vacature, de jobimport) en de vouw vanuit
// job_overrides (lib/crm/override-fold.mjs).
//
// Gepauzeerd zet ALLEEN een mens. De automaat die een vacature na zestig dagen
// zonder CV send op pauze zette is afgeschaft; zijn 1273 pauzes zijn weer open.
// Een klant die terugvalt naar prospect zet zijn vacatures op gesloten.
//
// Op een OPEN vacature hangen twee afgeleide onderlabels, puur uit de data,
// nergens opgeslagen (sinds 12 oktober 2026 NIET meer op de statuspil: die toont
// alleen Open, Gepauzeerd of Gesloten; de onderlabels blijven data voor de kolom
// Substatus en de filters):
//   'nieuw'           aangemaakt binnen JOB_NEW_DAYS.
//   'geen_activiteit' ouder dan JOB_NO_ACTIVITY_DAYS en in dat venster geen
//                     enkele CV send. Een vacature met een LOPENDE verkochte
//                     search (retained_slots zonder completed_at) valt hier
//                     nooit onder: daar is voor betaald en er loopt een
//                     campagne, stilte betekent daar iets anders.
// Nieuw wint van geen activiteit; overlappen kan toch niet, de vensters sluiten
// elkaar uit.
//
// Deze module is met opzet zuiver: geen React, geen databaseverbinding, alleen
// waarden, JS-functies en SQL-tekst. Elke leeskant (raster, zoekmachine,
// rapportage) hoort hier zijn expressie op te halen, zodat de drie waarden nooit
// weer per pagina uiteenlopen.

import { jobCategoryExpr } from './crm/candidate-sql.mjs';
import { activeEntityDeletionClause } from './deletion-visibility.mjs';

export const JOB_STATUS_VALUES = ['open', 'gepauzeerd', 'gesloten'];

export const JOB_NEW_DAYS = 45;
// Dezelfde zestig dagen als de afgeschafte pauzeerautomaat hanteerde, nu als
// LABEL in plaats van als schrijfactie: de vacature blijft open, hij krijgt
// alleen het onderlabel 'Geen activiteit'.
export const JOB_NO_ACTIVITY_DAYS = 60;

export const JOB_STATUS_LABEL = { open: 'Open', gepauzeerd: 'Gepauzeerd', gesloten: 'Gesloten' };
export const JOB_SUB_LABEL = { nieuw: 'Nieuw', geen_activiteit: 'Geen activiteit' };
// Tonen uit TonePill (components/HomeV2/lib.js): groen draagt gewicht, oranje
// waarschuwt, neutraal valt weg. Gesloten hoort weg te vallen.
export const JOB_STATUS_TONE = { open: 'green', gepauzeerd: 'amber', gesloten: 'neutral' };

// De ruwe RecruitCRM-statussen die als pauze tellen. De rest van de mirror
// (Closed, Canceled, Cancelled, Filled, Lost en alles wat we niet kennen) valt
// op gesloten: zonder eigen categorie is een vacature die niet Open of On Hold
// heet, klaar.
const LABEL_PAUSED = ['On Hold', 'Paused'];
const LABEL_OPEN = ['Open'];

/**
 * De JS-tweeling van jobStatusSql. `category` is de opgeloste app-categorie
 * (override boven mirror), `label` de ruwe job_status_label. Alleen een van de
 * drie waarden telt, plus de oude 'paused' als gepauzeerd; 'standard', 'hot' of
 * een onbekende categorie laat het label beslissen.
 */
export function normalizeJobStatus(category, label) {
  const c = String(category || '').trim().toLowerCase();
  if (JOB_STATUS_VALUES.includes(c)) return c;
  if (c === LEGACY_PAUSED) return 'gepauzeerd';
  const l = String(label || '').trim();
  if (LABEL_OPEN.includes(l)) return 'open';
  if (LABEL_PAUSED.includes(l)) return 'gepauzeerd';
  return 'gesloten';
}

// De enige oude categorie die nog telt: een mogelijk echte pauze.
const LEGACY_PAUSED = 'paused';

const quoted = (arr) => arr.map((v) => `'${v.replace(/'/g, "''")}'`).join(', ');

// De opgeloste categorie: job_overrides wint van de mirrorkolom. Zelfde
// gecorreleerde subselect als jobCategoryExpr, zodat elke query die een
// job-slug in scope heeft dit join-vrij kan inzetten.
const categoryEff = (jRef) => jobCategoryExpr(`${jRef}.slug`, `${jRef}.category`);

/** SQL-tekst die 'open', 'gepauzeerd' of 'gesloten' oplevert. */
export function jobStatusSql(jRef = 'j') {
  const cat = `lower(trim(COALESCE(${categoryEff(jRef)}, '')))`;
  const lab = `trim(COALESCE(${jRef}.job_status_label, ''))`;
  return `(CASE
      WHEN ${cat} IN (${quoted(JOB_STATUS_VALUES)}) THEN ${cat}
      WHEN ${cat} = '${LEGACY_PAUSED}' THEN 'gepauzeerd'
      WHEN ${lab} IN (${quoted(LABEL_OPEN)}) THEN 'open'
      WHEN ${lab} IN (${quoted(LABEL_PAUSED)}) THEN 'gepauzeerd'
      ELSE 'gesloten'
    END)`;
}

/** Boolean: staat de vacature open? */
export function jobOpenSql(jRef = 'j') {
  return `(${jobStatusSql(jRef)} = 'open')`;
}

/**
 * Boolean SQL: moet de boardpost van deze job dicht? De enige regel voor
 * het vacaturebord; de enige sluiter is closeDueBoardPosts (lib/board-close.mjs).
 * Waar zodra een van drie dingen geldt:
 *   - de job-rij bestaat niet meer (jRef.slug is NULL uit de LEFT JOIN);
 *   - er staat een deletions-rij voor de job: zacht verwijderd, gepurged, of
 *     meegenomen in de cascade van zijn bedrijf (reason 'cascade:<bedrijfsslug>');
 *   - de status is gesloten.
 * Gepauzeerd is NOOIT reden om te sluiten (eigenaarsbesluit 13 sep 2026): die post
 * blijft live. `slugRef` is de job-slug zoals de aanroeper hem heeft (bijvoorbeeld
 * p.job_slug), `jRef` de alias van een LEFT JOIN op rcrm_jobs.
 */
export function jobBoardCloseDueSql(slugRef, jRef = 'j') {
  return `(${jRef}.slug IS NULL
      OR NOT ${activeEntityDeletionClause(slugRef, 'job')}
      OR ${jobStatusSql(jRef)} = 'gesloten')`;
}

/** De laatste geslaagde CV send op de vacature. Een mislukte verzending telt niet. */
export function jobLastCvSentSql(jRef = 'j') {
  return `(SELECT max(cvs.sent_at) FROM cv_sends cvs WHERE cvs.job_slug = ${jRef}.slug AND cvs.status = 'sent')`;
}

/**
 * Open sinds: de laatste keer dat de vacature (weer) open ging, anders de
 * aanmaakdatum. Een heropening is een edit_log-regel op category naar 'open'
 * vanuit een andere status; zonder oude override-sleutel telt de bevroren
 * RecruitCRM-status als de oude waarde. edit_log begint op 7 okt 2026, dus een
 * eerdere heropening valt terug op created_on.
 */
export function jobOpenSinceSql(jRef = 'j') {
  const lab = `trim(COALESCE(${jRef}.job_status_label, ''))`;
  return `GREATEST(${jRef}.created_on, (SELECT max(josl.at) FROM edit_log josl
      WHERE josl.entity = 'job' AND josl.ref = ${jRef}.slug AND josl.field = 'category'
        AND lower(josl.new #>> '{}') = 'open'
        AND COALESCE(lower(josl.old #>> '{}'), CASE WHEN ${lab} IN (${quoted(LABEL_OPEN)}) THEN 'open' ELSE '' END) <> 'open'))`;
}

/** Boolean: open en jonger dan JOB_NEW_DAYS. */
export function jobIsNewSql(jRef = 'j') {
  return `(${jobOpenSql(jRef)} AND ${jRef}.created_on >= now() - interval '${JOB_NEW_DAYS} days')`;
}

/**
 * Boolean: open, ouder dan JOB_NO_ACTIVITY_DAYS en in dat venster geen CV send.
 * De leeftijdsclausule is er omdat een verse vacature nog nooit een CV send KAN
 * hebben gehad; zonder die regel zou elke nieuwe vacature meteen stil heten.
 * Een lopende verkochte search beschermt de vacature; een afgeronde (completed_at
 * gezet) niet meer.
 */
export function jobNoActivitySql(jRef = 'j') {
  const cutoff = `now() - interval '${JOB_NO_ACTIVITY_DAYS} days'`;
  return `(${jobOpenSql(jRef)}
      AND ${jRef}.created_on < ${cutoff}
      AND COALESCE(${jobLastCvSentSql(jRef)}, ${jRef}.created_on) < ${cutoff}
      AND NOT EXISTS (
        SELECT 1 FROM retained_slots rs
         WHERE rs.job_slug = ${jRef}.slug AND rs.completed_at IS NULL))`;
}

/** SQL-tekst die 'nieuw', 'geen_activiteit' of NULL oplevert. */
export function jobSubLabelSql(jRef = 'j') {
  return `(CASE
      WHEN ${jobIsNewSql(jRef)} THEN 'nieuw'
      WHEN ${jobNoActivitySql(jRef)} THEN 'geen_activiteit'
      ELSE NULL
    END)`;
}

const asTime = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const t = v instanceof Date ? v.getTime() : new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};
const days = (n) => n * 24 * 60 * 60 * 1000;

/** JS-tweeling van jobIsNewSql, zonder de open-check (die doet jobSubLabel). */
export function jobIsNew(createdOn, now = Date.now()) {
  const t = asTime(createdOn);
  return t !== null && t >= now - days(JOB_NEW_DAYS);
}

/** JS-tweeling van jobNoActivitySql, zonder de open-check. */
export function jobNoActivity({ createdOn, lastCvSentAt, retainedOpen = false } = {}, now = Date.now()) {
  if (retainedOpen) return false;
  const created = asTime(createdOn);
  if (created === null) return false;
  const cutoff = now - days(JOB_NO_ACTIVITY_DAYS);
  if (created >= cutoff) return false;
  const last = asTime(lastCvSentAt);
  return (last === null ? created : last) < cutoff;
}

/**
 * Het onderlabel van een rij uit de leesroutes: { status, created_on,
 * last_cv_sent_at, retained_open }. Alleen een open vacature krijgt er een.
 */
export function jobSubLabel(row, now = Date.now()) {
  if (!row || row.status !== 'open') return null;
  if (jobIsNew(row.created_on, now)) return 'nieuw';
  if (jobNoActivity({
    createdOn: row.created_on,
    lastCvSentAt: row.last_cv_sent_at,
    retainedOpen: !!row.retained_open,
  }, now)) return 'geen_activiteit';
  return null;
}

/**
 * De tekst die de gebruiker ziet: alleen Open, Gepauzeerd of Gesloten
 * (eigenaarsbesluit 12 oktober 2026). De onderlabels 'nieuw' en 'geen_activiteit'
 * blijven DATA (jobSubLabelSql, de kolom Substatus en de filters) maar staan niet
 * meer op de pil; de aanmaakdatum en de statuswijzigingen staan op de hover
 * (components/HomeV2/JobStatusHover.js). `sub` blijft als parameter staan zodat
 * de aanroepers niet hoeven te veranderen.
 */
export function jobStatusDisplay(status, sub) {
  return JOB_STATUS_LABEL[status] || JOB_STATUS_LABEL.gesloten;
}

/**
 * De korte vorm voor de vaste statuspil van een raster (StatusPill, 128 breed).
 * Sinds 12 oktober 2026 gelijk aan jobStatusDisplay: het onderlabel staat niet
 * meer op de pil, dus 'Open' past altijd.
 */
export function jobStatusShort(status, sub) {
  return jobStatusDisplay(status, sub);
}
